<template>
  <div
    v-if="fetchedCoverage"
    class="d-flex align-items-center flex-wrap fw-bold"
  >
    <div v-if="label" class="text-muted">{{ label }}&nbsp;</div>
    <div class="d-flex align-items-center">
      <div v-if="formattedStartDate" class="timestamp">
        {{ formattedStartDate }}
      </div>
      <div
        v-if="formattedStartDate && formattedEndDate"
        class="fw-bold mx-2 text-muted"
      >
        to
      </div>
      <div v-if="formattedEndDate" class="timestamp">
        {{ formattedEndDate }}
      </div>
    </div>
  </div>
</template>

<script>
import createHttp from "@/services/http";

export default {
  name: "DataTimestamp",
  emits: ["fetched"],
  props: {
    rsDataset: {
      type: String,
      required: true,
    },
    /**
     * An [`options` argument for `Intl.DateTimeFormat`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat#using_options)
     */
    options: {
      type: Object,
      default: () => ({
        // e.g. "2022-03-20" -> "2022"
        year: "numeric",
      }),
    },
    label: String,
  },
  data() {
    return {
      startDate: null,
      endDate: null,
      fetchedCoverage: false,
      alreadyFormatted: false,
    };
  },
  computed: {
    formattedStartDate() {
      return this.formatDate(this.startDate);
    },
    formattedEndDate() {
      return this.alreadyFormatted
        ? this.endDate
        : this.formatDate(this.endDate);
    },
  },
  methods: {
    /**
     * Returns `dateString` in the format specified by `this.options'
     * @param {string} dateString a string representing a date, in the format
     * "YYYY-MM-DD"
     */
    formatDate(dateString) {
      if (!dateString) return null;

      // Convert date-only string to `Date` with time for
      // that date at midnight in local timezone
      const date = new Date(dateString + "T00:00:00");
      return date.toLocaleDateString("en-US", this.options);
    },
    async getCoverage() {
      this.fetchedCoverage = false;
      if (this.rsDataset === "daily") {
        const date = new Date();
        this.endDate = date.toLocaleDateString("en-US", this.options);
        this.alreadyFormatted = true;
        this.fetchedCoverage = true;
      } else if (this.rsDataset === "weekly") {
        const date = new Date();
        // getDay() returns 0 for Sunday, ..., 6 for Saturday
        const dayOfWeek = date.getDay();
        // Calculates how many days have passed since the last Monday
        const daysSinceMonday = (dayOfWeek - 1 + 7) % 7;
        const lastMonday = new Date(date);
        // Take todays date and subtract by the days since monday to get Monday's date
        lastMonday.setDate(date.getDate() - daysSinceMonday);
        this.endDate = lastMonday.toLocaleDateString("en-US", this.options);
        this.alreadyFormatted = true;
        this.fetchedCoverage = true;
      } else {
        try {
          const response = await createHttp().get(
            `${import.meta.env.VITE_FLASK_URL}/api/data_coverage/${
              this.rsDataset
            }`
          );
          this.startDate = response.data.start_date;
          this.endDate = response.data.end_date;
          this.fetchedCoverage = true;
          this.$emit("fetched", {
            startDate: this.startDate,
            endDate: this.endDate,
          });
        } catch (error) {
          if (error.response?.status === 404) {
            console.error(`No coverage found for dataset ${this.rsDataset}`);
          } else {
            console.error(error);
          }
        }
      }
    },
  },
  watch: {
    rsDataset: {
      handler() {
        this.getCoverage();
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.timestamp {
  color: var(--S2NOceanBlue);
}
</style>
